import { Document, Image, Page, pdf, Text, View } from "@react-pdf/renderer"
import { styles } from "components"
import JsBarcode from "jsbarcode"
import moment from "moment"
import { cellValuePdf, RowExportTypePdf } from "types"
import { convertNumber } from "utils"
import { FormType } from "../../types"

type UseExportPdf = {
  data: FormType[]
}

type RowExportTypePdfWithArr = { [key: string]: any | Array<any> }

export const useExportPdf = ({ data }: UseExportPdf) => {
  const columns = [
    { header: "NO", key: "no", width: 5 },
    { header: "OPENING DATE", key: "od", width: 15 },
    { header: "BARCODE", key: "barcode", width: 25 },
    { header: "ITEM NAME", key: "name", width: 15 },
    { header: "PICTURES", key: "pictures", width: 15 },
    { header: "STOCK", key: "stock", width: 15 },
    { header: "UNIT", key: "unit", width: 15 },
    { header: "AVERAGE UNIT PRICE", key: "aup", width: 15 },
    { header: "LOCATION", key: "location", width: 15 }
  ]

  function numberFormat(value: number): cellValuePdf {
    return {
      text: convertNumber(value).intoCurrency,
      style: {
        textAlign: "right",
        fontWeight: "bold"
      }
    }
  }

  const dataMap: RowExportTypePdfWithArr[] = data.map((item, index) => {
    return {
      no: (index + 1).toString(),
      od: moment(item.transaction_date).format("DD/MM/YYYY"),
      barcode: item.product_barcode,
      name: item.product_name,
      stock: numberFormat(parseFloat(item.soh ?? "0")),
      unit: item.unit_name,
      aup: numberFormat(parseFloat(item.av_price_unit ?? "0")),
      location: item.location_name,
      pictures: item.picture
    }
  })

  return {
    handleExportPdf: () =>
      pdf(<PdfTemplate data={dataMap} column={columns} />)
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = `opening-stock.pdf`
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

function PdfTemplate({
  data,
  column
}: {
  data: RowExportTypePdf[]
  column: { header: string; key: string; width: number }[]
}) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>DATA ENTRY | OPENING STOCK</Text>
        <TableCell data={data} column={column} />
        <View
          style={{
            marginTop: 10,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end"
          }}
        />
      </Page>
    </Document>
  )
}

function TableCell({ data, column }: {
  data: RowExportTypePdfWithArr[]
  column: { header: string; key: string; width: number }[]
}) {
  const { REACT_APP_BASE_URL_API } = process.env
  const prefixImg = REACT_APP_BASE_URL_API + "/getimage/?imgpath="

  return (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        {column.map((col, index) => (
          <View key={index} style={[styles.tableCol, { flex: col.width }]}>
            <Text style={styles.headerText}>{col.header}</Text>
          </View>
        ))}
      </View>

      {/* Data */}
      {data.map((row, rowIndex) => (
        <View key={rowIndex} style={styles.tableRow}>
          {column.map((col, colIndex) => {
            const cellData = row[col.key]

            const isCellDataText = typeof cellData === "string" || typeof cellData === "number"

            const cellText = isCellDataText
              ? cellData
              : cellData?.text?.toString() ?? ""

            const cellStyle = typeof cellData === "object" && cellData?.style
              ? [styles.tableCell, cellData.style]
              : styles.tableCell

            const hasImage = row['pictures']?.length

            const image = prefixImg + row['pictures'][0]?.path

            const canvas = document.createElement("canvas")

            JsBarcode(canvas, row['barcode'], { format: "CODE39" })

            const base64 = canvas.toDataURL("image/png")

            return (
              <View
                key={colIndex}
                style={[styles.tableCol, { flex: col.width }]}>
                {col.key === 'barcode'
                  ? <Image src={base64} />
                  : col.key === 'pictures' && hasImage
                    ? <Image src={image} />
                    : <Text style={cellStyle} wrap={true}> {cellText}
                    </Text>
                }
              </View>
            )
          })}
        </View>
      ))}
    </View>
  )
}